import React, { useEffect, useMemo, useRef, useState } from "react";
import pagosService from "../../../../services/pagosService";
import { buildPrintHtml, printHtml } from "./PagoDetailPanel";
import { showToast } from "../../../../utils/showToast";
import { usePermissions } from "../../../../utils/permissions";
import { generarContratoInscripcion, descargarContratoInscripcion } from "../../../../services/contratosInscripcionService";
import { listarPersonas } from "../../../../services/personasService";

const ModalWrapper = ({ open, title, children, onClose }) => {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default function PagoFormModal({
  modal,
  form,
  onChange,
  closeModal,
  submit,
  submitting,
  matriculasOptions,
  loadingOptions,
  reloadOptions,
  matriculaInfo,
  loadingMatriculaInfo,
  searchTerm,
  setSearchTerm,
  lockMatricula,
  lockMonto,
  conceptos,
  metodos,
  payableItems = [],
  handleSelectItem,
  prefillMode = false,
  resultadoPago,
  selectedMetodo,
  cuentasBancarias = [],
}) {
  const { hasPermission } = usePermissions();
  const puedeEditarFecha = hasPermission("migracion.fecha_retroactiva");

  const [conceptoSearch, setConceptoSearch] = useState("");
  const [printLoading, setPrintLoading] = useState(false);
  const [contratoLoading, setContratoLoading] = useState(false);
  const prevConceptoIdRef = useRef(form.concepto_id);

  const montoCargosSeleccionados = (form.seleccion_items || [])
    .filter((i) => i.type === "cargo")
    .reduce((s, i) => s + Number(i.monto || 0), 0);

  const handlePrintComprobante = async (pagoId) => {
    if (!pagoId || printLoading) return;
    setPrintLoading(true);
    try {
      const data = await pagosService.obtenerComprobante(pagoId);
      const html = buildPrintHtml(data, "matricial76mm");
      printHtml(html);
    } catch {
      showToast({ type: "danger", message: "No se pudo obtener el comprobante." });
    } finally {
      setPrintLoading(false);
    }
  };

  const handleImprimirContrato = async (matriculaId) => {
    if (!matriculaId || contratoLoading) return;
    setContratoLoading(true);
    try {
      await generarContratoInscripcion(matriculaId);
      await descargarContratoInscripcion(matriculaId);
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "No se pudo generar el contrato." });
    } finally {
      setContratoLoading(false);
    }
  };
  const [conceptoMenuOpen, setConceptoMenuOpen] = useState(false);
  const [metodoSearch, setMetodoSearch] = useState("");
  const [metodoMenuOpen, setMetodoMenuOpen] = useState(false);
  const [docSugerencias, setDocSugerencias] = useState([]);
  const [docMenuOpen, setDocMenuOpen] = useState(false);

  const conceptoSeleccionado = conceptos?.find((c) => String(c.id) === String(form.concepto_id));
  const metodoSeleccionado = metodos?.find((m) => String(m.id) === String(form.metodo_id));
  const metodoInputValue = metodoSearch || metodoSeleccionado?.nombre || "";
  const metodoSugerencias = useMemo(() => {
    const term = metodoSearch.trim().toLowerCase();
    if (!term) return metodos || [];
    return (metodos || []).filter((m) => m.nombre.toLowerCase().includes(term));
  }, [metodos, metodoSearch]);

  // Valor que muestra el input: si hay selección muestra su nombre, si no lo que escribe
  const conceptoInputValue = conceptoSearch || conceptoSeleccionado?.nombre || "";

  // Sugerencias filtradas (mín. 2 chars o si el menú ya está abierto con poco texto)
  const conceptoSugerencias = useMemo(() => {
    const term = conceptoSearch.trim().toLowerCase();
    if (!term) return conceptos || [];
    return (conceptos || []).filter((c) =>
      c.nombre.toLowerCase().includes(term)
    );
  }, [conceptos, conceptoSearch]);

  // Resetear estado del combobox cuando se cierra el modal
  useEffect(() => {
    if (!modal) {
      setConceptoSearch("");
      setConceptoMenuOpen(false);
      setMetodoSearch("");
      setMetodoMenuOpen(false);
      setDocSugerencias([]);
      setDocMenuOpen(false);
    }
  }, [modal]);

  // Búsqueda debounced de personas por documento
  useEffect(() => {
    if (!modal || !form.facturar_tercero) return undefined;
    const term = (form.responsable_documento || "").trim();
    if (term.length < 4) { setDocSugerencias([]); return undefined; }
    let activo = true;
    const t = setTimeout(async () => {
      if (!activo) return;
      try {
        const data = await listarPersonas({ busqueda: term, limit: 6 });
        if (activo) setDocSugerencias(data.rows || []);
      } catch { /* silencioso */ }
    }, 350);
    return () => { activo = false; clearTimeout(t); };
  }, [modal, form.facturar_tercero, form.responsable_documento]);

  // Limpiar conceptoSearch cuando form.concepto_id cambia externamente (ej: handleSelectItem)
  useEffect(() => {
    if (form.concepto_id !== prevConceptoIdRef.current) {
      prevConceptoIdRef.current = form.concepto_id;
      setConceptoSearch("");
    }
  }, [form.concepto_id]);

  // Al abrir el modal, poner "Matrícula" y "Efectivo" por defecto
  useEffect(() => {
    if (modal && conceptos?.length && !form.concepto_id && !form.concepto_nombre) {
      const matricula = conceptos.find((c) => c.afecta_matricula === 1) || conceptos[0];
      if (matricula) onChange("concepto_id", String(matricula.id));
    }
  }, [modal, conceptos]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modal && metodos?.length && !form.metodo_id && !form.metodo_nombre) {
      onChange("metodo_id", String(metodos[0].id));
    }
  }, [modal, metodos]); // eslint-disable-line react-hooks/exhaustive-deps

  const optionsAvailable = matriculasOptions.length > 0;

  useEffect(() => {
    if (!modal) setSearchTerm("");
  }, [modal, setSearchTerm]);

  if (resultadoPago) {
    return (
      <ModalWrapper open={modal} title="Pago registrado" onClose={closeModal}>
        <div className="modal-body">
          <div className="alert alert-success mb-3">
            Pago registrado correctamente.
            {resultadoPago.cambio > 0 && ` Cambio: $${resultadoPago.cambio.toFixed(2)}`}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {resultadoPago.pagoId && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => handlePrintComprobante(resultadoPago.pagoId)}
                disabled={printLoading}
              >
                <i className="bi bi-printer me-2" />
                {resultadoPago.pagoIdCargos ? "Comprobante Matrícula" : "Imprimir Comprobante"}
              </button>
            )}
            {resultadoPago.pagoIdCargos && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handlePrintComprobante(resultadoPago.pagoIdCargos)}
                disabled={printLoading}
              >
                <i className="bi bi-printer me-2" />
                Comprobante Cargos
              </button>
            )}
            {resultadoPago.matriculaId && (
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={() => handleImprimirContrato(resultadoPago.matriculaId)}
                disabled={contratoLoading}
              >
                <i className="bi bi-file-earmark-text me-2" />
                {contratoLoading ? "Generando..." : "Imprimir Contrato"}
              </button>
            )}
          </div>
        </div>
        <div className="modal-footer border-0">
          <button type="button" className="btn btn-primary" onClick={closeModal}>
            Cerrar
          </button>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper open={modal} title="Registrar pago" onClose={closeModal}>
      <div className="modal-body">

        {/* Toggle: facturar a tercero */}
        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="facturar_tercero"
              checked={!!form.facturar_tercero}
              onChange={(e) => {
                onChange("facturar_tercero", e.target.checked);
                if (!e.target.checked) {
                  onChange("responsable_documento",  "");
                  onChange("responsable_tipo_doc",   "cedula");
                  onChange("responsable_nombres",    "");
                  onChange("responsable_apellidos",  "");
                  onChange("responsable_email",      "");
                  onChange("responsable_direccion",  "");
                  setDocSugerencias([]);
                  setDocMenuOpen(false);
                }
              }}
            />
            <label className="form-check-label" htmlFor="facturar_tercero">
              Facturar a otra persona
            </label>
          </div>
          {form.facturar_tercero && (
            <small className="text-muted">La factura saldrá a nombre del pagador, no del estudiante.</small>
          )}
        </div>

        {form.facturar_tercero && (
          <div className="mb-3 p-3 border rounded" style={{ background: "rgba(255,193,7,0.07)" }}>
            <div className="row g-2">
              {/* Tipo de documento */}
              <div className="col-md-4">
                <label className="form-label small mb-1">Tipo de documento</label>
                <select
                  className="form-select form-select-sm"
                  value={form.responsable_tipo_doc || "cedula"}
                  onChange={(e) => onChange("responsable_tipo_doc", e.target.value)}
                >
                  <option value="cedula">Cédula</option>
                  <option value="ruc">RUC</option>
                  <option value="pasaporte">Pasaporte</option>
                </select>
              </div>
              {/* Documento — buscador de personas existentes */}
              <div className="col-md-8 position-relative">
                <label className="form-label small mb-1">
                  {form.responsable_tipo_doc === "ruc" ? "RUC" : form.responsable_tipo_doc === "pasaporte" ? "N° Pasaporte" : "Cédula"} del pagador
                </label>
                <div className="input-group input-group-sm">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={form.responsable_tipo_doc === "ruc" ? "Ej: 0990012345001" : "Ej: 0900123456"}
                    value={form.responsable_documento || ""}
                    onFocus={() => { if (docSugerencias.length) setDocMenuOpen(true); }}
                    onBlur={() => setTimeout(() => setDocMenuOpen(false), 150)}
                    onChange={(e) => { onChange("responsable_documento", e.target.value); setDocMenuOpen(true); }}
                  />
                  <span className="input-group-text"><i className="bi bi-search" style={{ fontSize: 12 }} /></span>
                </div>
                {docMenuOpen && docSugerencias.length > 0 && (
                  <div className="list-group shadow-sm position-absolute w-100" style={{ zIndex: 1060, top: "100%", left: 0 }}>
                    {docSugerencias.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="list-group-item list-group-item-action py-1 px-2 small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          onChange("responsable_documento",  p.documento);
                          onChange("responsable_tipo_doc",   p.tipo_doc);
                          onChange("responsable_nombres",    p.razon_social);
                          onChange("responsable_apellidos",  "");
                          onChange("responsable_email",      p.email || "");
                          onChange("responsable_direccion",  p.direccion || "");
                          setDocMenuOpen(false);
                          setDocSugerencias([]);
                        }}
                      >
                        <span className="fw-semibold">{p.razon_social}</span>
                        <span className="text-muted ms-2">{p.documento}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Nombres */}
              <div className="col-md-6">
                <label className="form-label small mb-1">Nombres <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Ej: MARÍA GARCÍA"
                  value={form.responsable_nombres || ""}
                  onChange={(e) => onChange("responsable_nombres", e.target.value.toUpperCase())}
                />
              </div>
              {/* Apellidos */}
              <div className="col-md-6">
                <label className="form-label small mb-1">Apellidos</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Ej: LÓPEZ TORRES"
                  value={form.responsable_apellidos || ""}
                  onChange={(e) => onChange("responsable_apellidos", e.target.value.toUpperCase())}
                />
              </div>
              {/* Email */}
              <div className="col-md-6">
                <label className="form-label small mb-1">Email <span className="text-muted">(opcional)</span></label>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  placeholder="Para enviar el comprobante"
                  value={form.responsable_email || ""}
                  onChange={(e) => onChange("responsable_email", e.target.value)}
                />
              </div>
              {/* Dirección */}
              <div className="col-md-6">
                <label className="form-label small mb-1">Dirección <span className="text-muted">(opcional)</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Ej: Av. Principal 123"
                  value={form.responsable_direccion || ""}
                  onChange={(e) => onChange("responsable_direccion", e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <div className="row g-3">
          <div className="col-12">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Estudiante </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por cedula, nombres o apellidos"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        reloadOptions();
                      }
                    }}
                    disabled={(loadingOptions && !optionsAvailable) || lockMatricula || !!form.matricula_id}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={reloadOptions}
                    disabled={lockMatricula || !!form.matricula_id}
                    aria-label="Buscar"
                  >
                    <i className="bi bi-search" />
                  </button>
                  {(searchTerm || (form.seleccion_items || []).length > 0) && !lockMatricula && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setSearchTerm("");
                        onChange("matricula_id", "");
                        onChange("monto", "");
                        onChange("monto_total_cobro", null);
                        onChange("concepto_nombre", "");
                        onChange("cargo_id", null);
                        onChange("cargo_ids", []);
                        onChange("seleccion_items", []);
                        onChange("_lockMontoTotal", null);
                      }}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Concepto</label>
                <div className="position-relative">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ej: Matrícula, Camisa…"
                      value={conceptoInputValue}
                      onFocus={() => setConceptoMenuOpen(true)}
                      onBlur={() => setTimeout(() => setConceptoMenuOpen(false), 150)}
                      onChange={(e) => {
                        const val = e.target.value;
                        setConceptoSearch(val);
                        setConceptoMenuOpen(true);
                        // Al escribir, limpiar la selección y guardar como texto libre
                        onChange("concepto_id", "");
                        onChange("concepto_nombre", val);
                      }}
                    />
                    {conceptoInputValue && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="Limpiar"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setConceptoSearch("");
                          setConceptoMenuOpen(false);
                          onChange("concepto_id", "");
                          onChange("concepto_nombre", "");
                        }}
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    )}
                  </div>
                  {conceptoMenuOpen && (
                    <div className="list-group shadow-sm position-absolute w-100" style={{ zIndex: 1050, top: "100%" }}>
                      {conceptoSugerencias.length > 0 ? (
                        conceptoSugerencias.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className={`list-group-item list-group-item-action${String(c.id) === String(form.concepto_id) ? " active" : ""}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              onChange("concepto_id", String(c.id));
                              onChange("concepto_nombre", "");
                              setConceptoSearch("");
                              setConceptoMenuOpen(false);
                            }}
                          >
                            {c.nombre}
                          </button>
                        ))
                      ) : (
                        <div className="list-group-item text-muted small">
                          {conceptoSearch.trim()
                            ? `Se creará el concepto "${conceptoSearch.trim()}" al guardar.`
                            : "Sin coincidencias."}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {form.concepto_id && conceptoSeleccionado && !conceptoSeleccionado.afecta_matricula && (
                  <small className="text-muted d-block mt-1">Cobro adicional. No descuenta del precio del curso.</small>
                )}
                {!form.concepto_id && form.concepto_nombre && (
                  <small className="text-muted d-block mt-1">Concepto nuevo — se registrará al guardar.</small>
                )}
              </div>
              {loadingOptions && (
                <div className="col-12">
                  <small className="text-muted"><i className="bi bi-arrow-repeat spin me-1" />Buscando…</small>
                </div>
              )}
              {!loadingOptions && payableItems.length > 0 && (form.seleccion_items || []).length === 0 && (() => {
                const selectedKeys = new Set((form.seleccion_items || []).map((it) => it.key));
                return (
                  <div className="col-12">
                    <small className="text-muted d-block mb-1">
                      {prefillMode ? "Cobros seleccionados:" : "Selecciona el cobro a registrar:"}
                    </small>
                    <div className="list-group small pagos-matriculas-results">
                      {payableItems.map((item) => {
                        const checked = selectedKeys.has(item.key);
                        const isMatricula = item.type === "matricula";
                        const itemStyle = isMatricula
                          ? { background: "rgba(99,102,241,0.07)", borderColor: "rgba(99,102,241,0.25)" }
                          : { background: "rgba(255,193,7,0.1)", borderColor: "rgba(255,193,7,0.4)" };
                        const iconStyle = isMatricula ? { color: "#4f46e5" } : { color: "#b45309" };
                        if (prefillMode) {
                          // Modo prefilled: checkboxes para ver/modificar selección
                          return (
                            <label
                              key={item.key}
                              className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2${checked ? " active" : ""}`}
                              style={checked ? { cursor: "pointer" } : { ...itemStyle, cursor: "pointer" }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input flex-shrink-0 mt-0"
                                checked={checked}
                                onChange={() => handleSelectItem && handleSelectItem(item)}
                              />
                              <div className="me-auto">
                                <div className="fw-semibold">
                                  <i className={`bi bi-${isMatricula ? "mortarboard" : "box"} me-1`} style={checked ? {} : iconStyle} />
                                  {item.estudiante}
                                </div>
                                <div className={checked ? "opacity-75" : "text-muted"}>{item.label}</div>
                                <div className={checked ? "opacity-75" : "text-muted"}>{item.documento} · {item.codigo}</div>
                              </div>
                              <span className="fw-semibold text-nowrap">${Number(item.monto || 0).toFixed(2)}</span>
                            </label>
                          );
                        }
                        // Modo búsqueda normal: click directo, selección simple
                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center${checked ? " active" : ""}`}
                            style={checked ? {} : itemStyle}
                            onClick={() => handleSelectItem && handleSelectItem(item, { replace: true })}
                          >
                            <div className="me-auto">
                              <div className="fw-semibold">
                                <i className={`bi bi-${isMatricula ? "mortarboard" : "box"} me-1`} style={checked ? {} : iconStyle} />
                                {item.estudiante}
                              </div>
                              <div className={checked ? "opacity-75" : "text-muted small"}>{item.label}</div>
                              <div className={checked ? "opacity-75" : "text-muted small"}>{item.documento} · {item.codigo}</div>
                            </div>
                            <span className="fw-semibold text-nowrap">${Number(item.monto || 0).toFixed(2)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Monto recibido</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              placeholder="0.00"
              value={form.monto}
              onChange={(e) => onChange("monto", e.target.value)}
              disabled={lockMonto}
            />
            <small className="text-muted">
              {form._lockMontoTotal != null
                ? `Los cargos ($${montoCargosSeleccionados.toFixed(2)}) se cobran primero; el valor restante va a la matrícula. El monto debe ser mayor a $${montoCargosSeleccionados.toFixed(2)}.`
                : form.seleccion_items?.length > 1
                ? `Total de cobros: $${Number(form.monto_total_cobro || 0).toFixed(2)} — puedes ajustar si el cliente paga de más.`
                : conceptoSeleccionado && !conceptoSeleccionado.afecta_matricula
                  ? "Cobro adicional. No descuenta del precio del curso."
                  : "Si hay sobrante o saldo pendiente lo veras en el mensaje de exito."}
            </small>
          </div>

          <div className="col-md-6">
            <label className="form-label">Método de pago</label>
            <div className="position-relative">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Efectivo, Transferencia…"
                  value={metodoInputValue}
                  onFocus={() => setMetodoMenuOpen(true)}
                  onBlur={() => setTimeout(() => setMetodoMenuOpen(false), 150)}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMetodoSearch(val);
                    setMetodoMenuOpen(true);
                    onChange("metodo_id", "");
                    onChange("metodo_nombre", val);
                  }}
                />
                {metodoInputValue && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    title="Limpiar"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setMetodoSearch("");
                      setMetodoMenuOpen(false);
                      onChange("metodo_id", "");
                      onChange("metodo_nombre", "");
                    }}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
              {metodoMenuOpen && (
                <div className="list-group shadow-sm position-absolute w-100" style={{ zIndex: 1050, top: "100%" }}>
                  {metodoSugerencias.length > 0 ? (
                    metodoSugerencias.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className={`list-group-item list-group-item-action${String(m.id) === String(form.metodo_id) ? " active" : ""}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          onChange("metodo_id", String(m.id));
                          onChange("metodo_nombre", "");
                          setMetodoSearch("");
                          setMetodoMenuOpen(false);
                        }}
                      >
                        {m.nombre}
                      </button>
                    ))
                  ) : (
                    <div className="list-group-item text-muted small">
                      {metodoSearch.trim()
                        ? `Se creará el método "${metodoSearch.trim()}" al guardar.`
                        : "Sin coincidencias."}
                    </div>
                  )}
                </div>
              )}
            </div>
            <small className="text-muted d-block mt-1">La referencia se genera automáticamente.</small>
          </div>

          {/* Cuenta bancaria — solo para Transferencia / Depósito */}
          {(selectedMetodo?.valor === 'transferencia' || selectedMetodo?.valor === 'deposito') && cuentasBancarias.length > 0 && (
            <div className="col-12">
              <label className="form-label fw-semibold">
                <i className="bi bi-bank me-1 text-primary" />
                ¿A qué cuenta bancaria realizó la transferencia?
              </label>
              <select
                className="form-select"
                value={form.cuenta_bancaria_contifico_id || ''}
                onChange={e => onChange('cuenta_bancaria_contifico_id', e.target.value)}
              >
                <option value="">— Selecciona la cuenta destino —</option>
                {cuentasBancarias.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.nombre_banco ? `${c.nombre_banco} — ` : ''}{c.nombre} ({c.numero})
                  </option>
                ))}
              </select>
              {!form.cuenta_bancaria_contifico_id && (
                <small className="text-warning d-block mt-1">
                  <i className="bi bi-exclamation-triangle me-1" />
                  Sin cuenta seleccionada, el cobro en Contífico podría fallar.
                </small>
              )}
            </div>
          )}

          {/* Recargo por método de pago — solo para Tarjeta */}
          {selectedMetodo?.valor === 'tarjeta' && (() => {
            const montoBase = parseFloat(form.monto) || 0;
            const pct = parseFloat(form.recargo_porcentaje) || 0;
            const recargo = pct > 0 && montoBase > 0 ? parseFloat((montoBase * pct / 100).toFixed(2)) : 0;
            const total = montoBase + recargo;
            return (
              <div className="col-12">
                <div className="p-3 border rounded" style={{ background: "rgba(99,102,241,0.05)" }}>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label mb-1 small fw-semibold">Recargo por método (%)</label>
                      <div className="input-group input-group-sm">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          className="form-control"
                          placeholder="0.00"
                          value={form.recargo_porcentaje}
                          onChange={(e) => onChange("recargo_porcentaje", e.target.value)}
                        />
                        <span className="input-group-text">%</span>
                        {parseFloat(form.recargo_porcentaje) > 0 && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            title="Quitar recargo"
                            onClick={() => onChange("recargo_porcentaje", "")}
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                        )}
                      </div>
                      <small className="text-muted">Dejar en 0 si no aplica recargo.</small>
                    </div>
                    {montoBase > 0 && (
                      <div className="col-md-8">
                        <div className="d-flex flex-wrap gap-3 small">
                          <div>
                            <span className="text-muted">Valor del cobro</span>
                            <div className="fw-semibold">${montoBase.toFixed(2)}</div>
                          </div>
                          {recargo > 0 && (
                            <div>
                              <span className="text-muted">Recargo ({pct}%)</span>
                              <div className="fw-semibold text-warning">+${recargo.toFixed(2)}</div>
                            </div>
                          )}
                          {recargo > 0 && (
                            <div>
                              <span className="text-muted">Total a cobrar al estudiante</span>
                              <div className="fw-bold fs-6" style={{ color: "#4f46e5" }}>${total.toFixed(2)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {(loadingMatriculaInfo || matriculaInfo) && (
            <div className="col-12">
              {loadingMatriculaInfo && (
                <small className="text-muted"><i className="bi bi-arrow-repeat spin me-1" />Cargando...</small>
              )}
              {!loadingMatriculaInfo && matriculaInfo && (
                <div className="d-flex align-items-center flex-wrap gap-2 small">
                  <i className="bi bi-bookmark-fill text-muted" />
                  <span className="text-muted">{matriculaInfo.promocion}</span>
                  <span className="vr" />
                  <span className={`badge ${matriculaInfo.estado_pago === 'pagado' ? 'bg-success' : matriculaInfo.estado_pago === 'parcial' ? 'bg-info text-dark' : 'bg-warning text-dark'}`}>
                    {matriculaInfo.estado_pago?.toUpperCase()}
                  </span>
                  <span className="badge bg-secondary">{matriculaInfo.estado_matricula?.toUpperCase()}</span>
                </div>
              )}
            </div>
          )}

          {form.seleccion_items?.length >= 1 && (() => {
            const subtotalItems = Number(form.monto_total_cobro || 0);
            const montoBase = parseFloat(form.monto) || 0;
            const recargoResumenPct = selectedMetodo?.valor === 'tarjeta' ? parseFloat(form.recargo_porcentaje) || 0 : 0;
            const recargoResumenMonto = recargoResumenPct > 0 && montoBase > 0
              ? parseFloat((montoBase * recargoResumenPct / 100).toFixed(2))
              : 0;
            const totalConRecargo = (montoBase > 0 ? montoBase : subtotalItems) + recargoResumenMonto;
            return (
              <div className="col-12">
                <div className="alert border-0 mb-0 py-2" style={{ background: "rgba(99,102,241,0.07)", borderRadius: "12px" }}>
                  <p className="fw-semibold small mb-2" style={{ color: "#4f46e5" }}>
                    <i className="bi bi-cart-check me-2" />
                    Resumen de cobro
                  </p>
                  {form.seleccion_items.map((item, i) => (
                    <div key={i} className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">{item.label}</span>
                      <strong>${Number(item.monto || 0).toFixed(2)}</strong>
                    </div>
                  ))}
                  {recargoResumenMonto > 0 && (
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">Recargo tarjeta ({recargoResumenPct}%)</span>
                      <strong className="text-warning">+${recargoResumenMonto.toFixed(2)}</strong>
                    </div>
                  )}
                  <hr className="my-1" />
                  <div className="d-flex justify-content-between fw-bold">
                    <span>Total a recibir del cliente</span>
                    <span style={{ color: "#4f46e5" }}>${totalConRecargo.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {puedeEditarFecha && (
          <div className="mt-3 p-2 border rounded bg-warning-subtle">
            <label className="form-label mb-1" style={{ fontSize: "0.8rem" }}>Fecha de pago (migración)</label>
            <input
              type="datetime-local"
              className="form-control form-control-sm"
              value={form.fecha_pago || ""}
              onChange={(e) => onChange("fecha_pago", e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="modal-footer border-0">
        <button type="button" className="btn btn-outline-secondary" onClick={closeModal} disabled={submitting}>
          Cancelar
        </button>
        <button type="button" className="btn btn-primary" onClick={submit} disabled={submitting}>
          {submitting ? (
            <>
              <i className="bi bi-arrow-repeat me-2 spin" />
              Guardando...
            </>
          ) : (
            <>
              <i className="bi bi-cash-stack me-2" />
              Registrar pago
            </>
          )}
        </button>
      </div>
    </ModalWrapper>
  );
}
